import React, { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { BsCloudUpload } from "react-icons/bs";
import { FaArrowLeft } from "react-icons/fa";
import { MdDeleteForever } from "react-icons/md";
import ActionButton from "../../Common/ActionButton";
import { InputField, TextAreaField } from "../../Common/FormComponents";
import { getallMaterial } from "../../../Redux/actions/Operationsactions/getmaterial";
import { getallStock } from "../../../Redux/actions/Operationsactions/getstock";
import { AddStockAdjustmentApi } from "../../../Api/Store/StockAdjustment";

/* ─── Reason Codes List ─────────────────────────────────────────────────── */
const REASON_CODES = [
  "Natural Site Wastage / Spillage (Cement tearing, sand wash-out)",
  "Transit & Offloading Damage (Broken tiles, broken bricks)",
  "Audit Discrepancy (Discovered during physical inventory audits)",
  "Weather Damage (Hardened cement due to rain)",
  "Theft / Pilferage",
  "Data Entry Error Corrected",
];

/* ─── Initial empty line item ─────────────────────────────────────────────── */
const emptyLine = () => ({
  id: Date.now() + Math.random(),
  MaterialId: "",
  MaterialPackagingId: "",
  Type: "Decrease",
  Quantity: "",
  ReasonCode: "",
  Remarks: "",
});

const AddStockAdjustment = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const materialData = useSelector((state) => state.getAllMaterial?.data || []);
  const stockData = useSelector((state) => state.getAllStock?.data || []);

  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    dispatch(getallMaterial());
    dispatch(getallStock());
  }, [dispatch]);

  /* Material.Id -> Stock row (Quantity + Batches, in the material's base unit). */
  const stockByMaterialId = useMemo(() => {
    const map = {};
    stockData.forEach((s) => {
      map[s.MaterialId] = s;
    });
    return map;
  }, [stockData]);

  /* ── Header form ── */
  const [formData, setFormData] = useState({
    AdjustmentDate: "",
    Note: "",
  });

  /* ── List of line items ── */
  const [lineItems, setLineItems] = useState([emptyLine()]);

  /* ── Handlers ── */
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddRow = () => {
    setLineItems((prev) => [...prev, emptyLine()]);
  };

  const handleRemoveRow = (idx) => {
    setLineItems((prev) => prev.filter((_, i) => i !== idx));
  };

  const getAvailableStockInUnit = (materialId, packagingId) => {
    if (!materialId) {
      return 0;
    }
    const material = materialData.find((m) => m.Id === materialId);
    const packaging = material?.Packagings?.find((p) => p.Id === packagingId);
    const stock = stockByMaterialId[materialId];
    const baseAvailable = stock?.Quantity || 0;
    const conversion = packaging?.ConversionToBase;
    return conversion ? baseAvailable / conversion : baseAvailable;
  };

  const handleLineChange = (idx, field, value) => {
    setLineItems((prev) => {
      const updated = [...prev];
      const currentItem = { ...updated[idx], [field]: value };

      // Auto select Type based on entered Quantity vs Available Stock
      if (field === "Quantity") {
        if (value !== "" && !isNaN(value) && currentItem.MaterialId) {
          const num = Number(value);
          const avail = getAvailableStockInUnit(
            currentItem.MaterialId,
            currentItem.MaterialPackagingId,
          );
          if (num > avail) {
            currentItem.Type = "Increase"; // Auto select (+) Add
          } else {
            currentItem.Type = "Decrease"; // Auto select (-) Deduct
          }
        }
      }

      // If unit changed and quantity is present, re-check
      if (field === "MaterialPackagingId") {
        if (currentItem.Quantity !== "" && !isNaN(currentItem.Quantity) && currentItem.MaterialId) {
          const num = Number(currentItem.Quantity);
          const avail = getAvailableStockInUnit(currentItem.MaterialId, value);
          if (num > avail) {
            currentItem.Type = "Increase";
          } else {
            currentItem.Type = "Decrease";
          }
        }
      }

      updated[idx] = currentItem;
      return updated;
    });
  };

  const handleLineMaterialChange = (idx, materialId) => {
    setLineItems((prev) => {
      const updated = [...prev];
      const currentItem = {
        ...updated[idx],
        MaterialId: materialId,
        MaterialPackagingId: "",
      };

      if (currentItem.Quantity !== "" && !isNaN(currentItem.Quantity) && materialId) {
        const num = Number(currentItem.Quantity);
        const avail = getAvailableStockInUnit(materialId, "");
        if (num > avail) {
          currentItem.Type = "Increase";
        } else {
          currentItem.Type = "Decrease";
        }
      }

      updated[idx] = currentItem;
      return updated;
    });
  };

  const handleSave = async (e) => {
    e.preventDefault();

    if (!formData.AdjustmentDate) {
      toast.error("Date is required");
      return;
    }
    if (lineItems.length === 0) {
      toast.error("Please add at least one line item");
      return;
    }

    for (let i = 0; i < lineItems.length; i++) {
      const item = lineItems[i];
      if (!item.MaterialId) {
        toast.error(`Material is required on row ${i + 1}`);
        return;
      }
      if (!item.Type) {
        toast.error(`Adjustment Type is required on row ${i + 1}`);
        return;
      }
      if (!item.Quantity || Number(item.Quantity) <= 0) {
        toast.error(`Quantity must be greater than 0 on row ${i + 1}`);
        return;
      }
      if (!item.ReasonCode) {
        toast.error(`Reason Code is required on row ${i + 1}`);
        return;
      }

      const material = materialData.find((m) => m.Id === item.MaterialId);
      const packaging = material?.Packagings?.find((p) => p.Id === item.MaterialPackagingId);
      const stock = stockByMaterialId[item.MaterialId];

      if (item.Type === "Decrease") {
        const baseAvailable = stock?.Quantity || 0;
        const availableInUnit =
          packaging && packaging.ConversionToBase
            ? baseAvailable / packaging.ConversionToBase
            : baseAvailable;

        if (Number(item.Quantity) > availableInUnit) {
          toast.error(
            `Row ${i + 1}: quantity exceeds available stock (${availableInUnit.toFixed(2)} ${
              packaging?.UomName || material?.UomName || ""
            })`,
          );
          return;
        }
      } else if (item.Type === "Increase") {
        const baseRestorable = (stock?.Batches || []).reduce(
          (sum, b) => sum + (b.QtConsumed || 0),
          0,
        );
        const restorableInUnit =
          packaging && packaging.ConversionToBase
            ? baseRestorable / packaging.ConversionToBase
            : baseRestorable;

        if (Number(item.Quantity) > restorableInUnit) {
          toast.error(
            `Row ${i + 1}: quantity is more than the stock on hand, so it was treated as (+) Add. ` +
              `Add can only restore previously consumed stock (max ${restorableInUnit.toFixed(2)} ${
                packaging?.UomName || material?.UomName || ""
              }). To deduct, enter a quantity up to the stock on hand and choose (-) Deduct.`,
          );
          return;
        }
      }
    }

    try {
      setIsLoading(true);

      const payload = {
        adjustmentDate: formData.AdjustmentDate,
        note: formData.Note,
        lines: lineItems.map((item) => ({
          materialId: item.MaterialId,
          materialPackagingId: item.MaterialPackagingId || null,
          type: item.Type,
          quantity: Number(item.Quantity),
          reason: item.ReasonCode || "",
          reasonCode: item.ReasonCode || "",
          remarks: item.Remarks || "",
        })),
      };

      const res = await AddStockAdjustmentApi(payload);
      if (res?.data?.IsSuccess) {
        toast.success(res.data.Message || "Stock adjustment added successfully");
        dispatch(getallStock());
        history.push("/store/stock");
      } else {
        toast.error(res?.data?.Message || "Failed to add stock adjustment");
      }
    } catch (err) {
      toast.error("Something went wrong");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      {/* ── Page Header ── */}
      <div className="row">
        <div className="col-md-12 text-left page_heading">
          <div className="d-flex align-items-center">
            <FaArrowLeft
              size={16}
              style={{ cursor: "pointer", marginRight: "12px" }}
              title="Back"
              onClick={() => history.goBack()}
            />
            <h4 className="m-0">Add Stock Adjustment</h4>
          </div>
        </div>
      </div>

      <form onSubmit={handleSave} autoComplete="off">
        {/* ── Header Fields ── */}
        <div className="row mb-2">
          <InputField
            label="Adjustment Date:"
            name="AdjustmentDate"
            type="date"
            value={formData.AdjustmentDate}
            onChange={handleFormChange}
            required
          />

          <TextAreaField
            label="Note:"
            name="Note"
            value={formData.Note}
            onChange={handleFormChange}
            rows={2}
          />
        </div>

        {/* ── Adjustment Line Items Section matching reference image ── */}
        <div className="mt-4">
          <h5 style={{ fontWeight: "600", color: "#1e293b", marginBottom: "14px" }}>
            Adjustment Line Items
          </h5>

          <div
            style={{
              backgroundColor: "#ffffff",
              border: "1px solid #e2e8f0",
              borderRadius: "10px",
              overflow: "hidden",
              boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
            }}
          >
            <div className="table-responsive" style={{ margin: 0 }}>
              <table
                className="table table-hover mb-0"
                style={{ fontSize: "12px", borderCollapse: "collapse" }}
              >
                <thead style={{ backgroundColor: "#f8fafc", borderBottom: "1.5px solid #e2e8f0" }}>
                  <tr>
                    <th
                      style={{
                        padding: "12px 14px",
                        color: "#334155",
                        fontWeight: "500",
                        width: "21%",
                      }}
                    >
                      Material <span style={{ color: "#ef4444" }}>*</span>
                    </th>
                    <th
                      style={{
                        padding: "12px 14px",
                        color: "#334155",
                        fontWeight: "500",
                        width: "13%",
                      }}
                    >
                      Unit (UOM)
                    </th>
                    <th
                      style={{
                        padding: "12px 14px",
                        color: "#334155",
                        fontWeight: "500",
                        width: "11%",
                      }}
                    >
                      System Stock
                    </th>
                    <th
                      style={{
                        padding: "12px 14px",
                        color: "#334155",
                        fontWeight: "500",
                        width: "13%",
                      }}
                    >
                      Adjustment Type <span style={{ color: "#ef4444" }}>*</span>
                    </th>
                    <th
                      style={{
                        padding: "12px 14px",
                        color: "#334155",
                        fontWeight: "500",
                        width: "9%",
                      }}
                    >
                      Quantity <span style={{ color: "#ef4444" }}>*</span>
                    </th>
                    <th
                      style={{
                        padding: "12px 14px",
                        color: "#334155",
                        fontWeight: "500",
                        width: "16%",
                      }}
                    >
                      Reason Code <span style={{ color: "#ef4444" }}>*</span>
                    </th>
                    <th
                      style={{
                        padding: "12px 14px",
                        color: "#334155",
                        fontWeight: "500",
                        width: "17%",
                      }}
                    >
                      Line Remarks
                    </th>
                    <th
                      className="text-center"
                      style={{
                        padding: "12px 14px",
                        color: "#334155",
                        fontWeight: "500",
                        width: "6%",
                      }}
                    >
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {lineItems.map((item, idx) => {
                    const selectedMaterial = materialData.find((m) => m.Id === item.MaterialId);
                    const packagingOptions = selectedMaterial?.Packagings || [];
                    const stock = stockByMaterialId[item.MaterialId];
                    const baseAvailable = stock?.Quantity || 0;

                    const selectedPackaging = packagingOptions.find(
                      (p) => p.Id === item.MaterialPackagingId,
                    );
                    const unitLabel = selectedPackaging?.UomName || selectedMaterial?.UomName || "";
                    const conversion = selectedPackaging?.ConversionToBase;
                    const availableInSelectedUnit = conversion
                      ? baseAvailable / conversion
                      : baseAvailable;

                    const displayStock =
                      availableInSelectedUnit % 1 === 0
                        ? availableInSelectedUnit.toString()
                        : availableInSelectedUnit.toFixed(1);

                    return (
                      <tr key={item.id || idx} style={{ borderBottom: "1px solid #f1f5f9" }}>
                        {/* 1. Material */}
                        <td style={{ padding: "10px 12px", verticalAlign: "middle" }}>
                          <select
                            name="MaterialId"
                            value={item.MaterialId || ""}
                            onChange={(e) => handleLineMaterialChange(idx, e.target.value)}
                            className="form-control form-control-sm"
                            style={{
                              height: "36px",
                              borderRadius: "6px",
                              fontSize: "13px",
                              border: "1px solid #cbd5e1",
                            }}
                          >
                            <option value="">Select Material</option>
                            {materialData.map((m) => (
                              <option key={m.Id} value={m.Id}>
                                {m.ItemName}
                              </option>
                            ))}
                          </select>
                        </td>

                        {/* 2. Unit (UOM) */}
                        <td style={{ padding: "10px 12px", verticalAlign: "middle" }}>
                          <select
                            name="MaterialPackagingId"
                            value={item.MaterialPackagingId || ""}
                            onChange={(e) =>
                              handleLineChange(idx, "MaterialPackagingId", e.target.value)
                            }
                            className="form-control form-control-sm"
                            style={{
                              height: "36px",
                              borderRadius: "6px",
                              fontSize: "13px",
                              border: "1px solid #cbd5e1",
                              backgroundColor:
                                !selectedMaterial || packagingOptions.length === 0
                                  ? "#f8fafc"
                                  : "#ffffff",
                            }}
                            disabled={!selectedMaterial || packagingOptions.length === 0}
                          >
                            <option value="">
                              {selectedMaterial ? selectedMaterial.UomName : "Unit (UOM)"}
                            </option>
                            {packagingOptions.map((p) => (
                              <option key={p.Id} value={p.Id}>
                                {p.PackagingName ? `${p.PackagingName} (${p.UomName})` : p.UomName}
                              </option>
                            ))}
                          </select>
                        </td>

                        {/* 3. System Stock Badge */}
                        <td
                          style={{
                            padding: "10px 12px",
                            verticalAlign: "middle",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {item.MaterialId ? (
                            <span
                              style={{
                                display: "inline-block",
                                backgroundColor: "#e2e8f0",
                                color: "#334155",
                                borderRadius: "14px",
                                padding: "4px 12px",
                                fontWeight: "600",
                                fontSize: "12px",
                              }}
                            >
                              {displayStock} {unitLabel}
                            </span>
                          ) : (
                            <span style={{ color: "#94a3b8", fontSize: "12px" }}>-</span>
                          )}
                        </td>

                        {/* 4. Adjustment Type */}
                        <td style={{ padding: "10px 12px", verticalAlign: "middle" }}>
                          <select
                            name="Type"
                            value={item.Type || ""}
                            onChange={(e) => handleLineChange(idx, "Type", e.target.value)}
                            className="form-control form-control-sm"
                            style={{
                              height: "36px",
                              borderRadius: "6px",
                              fontSize: "12.5px",
                              fontWeight: "600",
                              border: "1px solid",
                              borderColor:
                                item.Type === "Decrease"
                                  ? "#fecdd3"
                                  : item.Type === "Increase"
                                    ? "#bbf7d0"
                                    : "#cbd5e1",
                              backgroundColor:
                                item.Type === "Decrease"
                                  ? "#fff1f2"
                                  : item.Type === "Increase"
                                    ? "#f0fdf4"
                                    : "#ffffff",
                              color:
                                item.Type === "Decrease"
                                  ? "#e11d48"
                                  : item.Type === "Increase"
                                    ? "#16a34a"
                                    : "#334155",
                              cursor: "pointer",
                            }}
                          >
                            <option value="">Select Type</option>
                            <option value="Decrease">(-) Deduct</option>
                            <option value="Increase">(+) Add</option>
                          </select>
                        </td>

                        {/* 5. Quantity */}
                        <td style={{ padding: "10px 12px", verticalAlign: "middle" }}>
                          <input
                            type="number"
                            name="Quantity"
                            value={item.Quantity ?? ""}
                            onChange={(e) => handleLineChange(idx, "Quantity", e.target.value)}
                            className="form-control form-control-sm text-right"
                            style={{
                              height: "36px",
                              borderRadius: "6px",
                              fontSize: "13px",
                              fontWeight: "600",
                              border: "1px solid #cbd5e1",
                              minWidth: "75px",
                            }}
                            placeholder="0"
                            min={0}
                            step="any"
                          />
                        </td>

                        {/* 6. Reason Code */}
                        <td style={{ padding: "10px 12px", verticalAlign: "middle" }}>
                          <select
                            name="ReasonCode"
                            value={item.ReasonCode || ""}
                            onChange={(e) => handleLineChange(idx, "ReasonCode", e.target.value)}
                            className="form-control form-control-sm"
                            style={{
                              height: "36px",
                              borderRadius: "6px",
                              fontSize: "12.5px",
                              border: "1px solid #cbd5e1",
                              color: item.ReasonCode ? "#1e293b" : "#94a3b8",
                            }}
                          >
                            <option value="">Select Reason</option>
                            {REASON_CODES.map((code) => (
                              <option key={code} value={code} style={{ color: "#1e293b" }}>
                                {code}
                              </option>
                            ))}
                          </select>
                        </td>

                        {/* 7. Line Remarks */}
                        <td style={{ padding: "10px 12px", verticalAlign: "middle" }}>
                          <textarea
                            name="Remarks"
                            rows={1}
                            value={item.Remarks || ""}
                            onChange={(e) => handleLineChange(idx, "Remarks", e.target.value)}
                            className="form-control form-control-sm"
                            style={{
                              borderRadius: "6px",
                              fontSize: "12.5px",
                              border: "1px solid #cbd5e1",
                              resize: "vertical",
                              minHeight: "36px",
                              maxHeight: "65px",
                              lineHeight: "1.3",
                            }}
                            placeholder="e.g. Damage notes, audit details..."
                          />
                        </td>

                        {/* 8. Action */}
                        <td
                          className="text-center"
                          style={{ padding: "10px 12px", verticalAlign: "middle" }}
                        >
                          <span
                            onClick={() => handleRemoveRow(idx)}
                            style={{
                              cursor: "pointer",
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                            title="Remove row"
                          >
                            <MdDeleteForever
                              size="1.4em"
                              style={{ color: "#dc3545", transition: "transform 0.15s ease" }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.transform = "scale(1.15)";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.transform = "scale(1)";
                              }}
                            />
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Add Line Item Button */}
          <div className="mt-3">
            <button type="button" onClick={handleAddRow} className="bulk-add-btn">
              <span style={{ fontSize: "16px", lineHeight: "1" }}>+</span>
              <span>Add Line Item</span>
            </button>
          </div>
        </div>

        {/* ── Footer Buttons ── */}
        <div className="float-right mt-4 mr-md-3">
          <ActionButton
            text="Cancel"
            variant="cancel"
            onClick={() => history.push("/store/stock")}
          />
          <ActionButton text="Save" icon={BsCloudUpload} type="submit" isLoading={isLoading} />
        </div>
      </form>
    </div>
  );
};

export default AddStockAdjustment;
